import React, { useState } from 'react';
import { Language as LanguageIcon } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCart } from '../contexts/CartContext';
import { useProjectWorkflow } from '../contexts/ProjectWorkflowContext';
import { useAuth } from '../contexts/AuthContext';
import { cartService } from '../services/cartService';
import { clientPlanService } from '../services/clientPlanService';
import CheckoutModal from '../components/cart/CheckoutModal';
import LoadingSpinner from '../components/common/LoadingSpinner';

function siteHref(url: string): string {
  const raw = (url || '').trim();
  if (!raw) return '#';
  return /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
}

function siteDisplayLabel(url: string, maxLen = 52): string {
  let label = (url || '').trim().replace(/^https?:\/\//i, '').replace(/^www\./i, '');
  if (!label) return url || '';
  if (label.length <= maxLen) return label;
  const head = Math.ceil((maxLen - 1) * 0.55);
  const tail = maxLen - head - 1;
  return `${label.slice(0, head)}…${label.slice(-tail)}`;
}

const PlaceOrderPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { selectedProject, goToStep, markStepDone } = useProjectWorkflow();
  const { isClient, isAgency } = useAuth();
  const usesPaymentCheckout = isClient || isAgency;
  const projectId = searchParams.get('projectId') || '';
  const planId = searchParams.get('planId') || '';
  const { cart, loading, refreshCart } = useCart(projectId || undefined);

  const [drafts, setDrafts] = useState<Record<string, { contentUrl: string; contentText: string }>>({});
  const [attemptedProceed, setAttemptedProceed] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const checkoutSucceededRef = React.useRef(false);

  const fulfillSavedOrder = async () => {
    if (!planId) return;
    try {
      await clientPlanService.fulfillSavedOrder(planId);
      await queryClient.invalidateQueries({ queryKey: ['wf-saved-plans'] });
    } catch {
      /* non-critical cleanup */
    }
  };

  const items = cart?.cartItems || [];

  const isValidUrl = (value: string) => {
    const v = (value || '').trim();
    if (!v) return false;
    try {
      const parsed = new URL(v);
      return /^https?:$/i.test(parsed.protocol);
    } catch {
      return false;
    }
  };

  const effectiveUrl = (item: { id: string; contentUrl?: string | null }) =>
    drafts[item.id]?.contentUrl ?? item.contentUrl ?? '';

  const missingDocCount = items.filter((it) => !isValidUrl(effectiveUrl(it))).length;
  const allDocsProvided = items.length > 0 && missingDocCount === 0;

  const updateMutation = useMutation({
    mutationFn: ({ id, contentUrl, contentText }: { id: string; contentUrl?: string; contentText?: string }) =>
      cartService.updateCartItem(id, {
        contentUrl: contentUrl || undefined,
        contentDocUrl: null,
        contentText: contentText || undefined,
      }),
    onSuccess: () => {
      refreshCart();
      queryClient.invalidateQueries({ queryKey: ['cart'] });
    },
  });

  if (!projectId) {
    return <p style={{ color: '#64748B' }}>Select a project first.</p>;
  }

  if (loading && !cart) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner />
      </div>
    );
  }

  const total = items.reduce((t: number, i: { price?: number }) => t + (Number(i.price) || 0), 0);

  const getDraft = (id: string) =>
    drafts[id] || {
      contentUrl: '',
      contentText: '',
    };

  const handleSaveRow = async (itemId: string) => {
    const d = getDraft(itemId);
    const line = items.find((i: { id: string }) => i.id === itemId);
    if (!line) return;
    setDrafts((prev) => ({
      ...prev,
      [itemId]: {
        contentUrl: d.contentUrl || line.contentUrl || '',
        contentText: d.contentText || line.contentText || '',
      },
    }));
    await updateMutation.mutateAsync({
      id: itemId,
      contentUrl: d.contentUrl.trim() || undefined,
      contentText: d.contentText.trim() || undefined,
    });
  };

  return (
    <div className="wf-step-page">
      <div className="wf-step-header">
        <div>
          <h1 className="wf-step-title">Place order</h1>
          <p className="wf-step-subtitle">
            Add a Google Doc link and optional comments for each site in your cart.
          </p>
        </div>
        <div className="wf-step-total-card">
          <div className="wf-step-total-card-label">Order total</div>
          <div className="wf-step-total-card-value">${total}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="wf-step-empty">
          <p style={{ marginBottom: 20 }}>Your cart is empty for this project.</p>
          <button
            type="button"
            className="wf-btn-create"
            onClick={() => goToStep('sites')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', fontSize: 14 }}
          >
            <LanguageIcon sx={{ fontSize: 18 }} />
            Explore sites
          </button>
        </div>
      ) : (
        <>
          <div className="sites-table-container">
            <table className="sites-table wf-step-place-order-table">
              <colgroup>
                <col className="col-site" />
                <col className="col-doc" />
                <col className="col-comments" />
              </colgroup>
              <thead>
                <tr>
                  <th>Site</th>
                  <th>
                    Content doc link <span style={{ color: '#DC2626' }}>*</span>
                  </th>
                  <th>Additional comments</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => {
                  const contentUrl = drafts[item.id]?.contentUrl ?? item.contentUrl ?? '';
                  const contentText = drafts[item.id]?.contentText ?? item.contentText ?? '';
                  const docMissing = attemptedProceed && !isValidUrl(contentUrl);
                  const fullSiteUrl = (item.siteUrl || '').trim();
                  return (
                    <tr key={item.id}>
                      <td className="wf-step-place-order-site-cell">
                        <a
                          href={siteHref(fullSiteUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="wf-step-site-link"
                          title={fullSiteUrl}
                        >
                          {siteDisplayLabel(fullSiteUrl)}
                        </a>
                        <div className="wf-step-price" style={{ fontSize: 12, marginTop: 4 }}>
                          ${item.price}
                        </div>
                      </td>
                      <td>
                        <input
                          type="url"
                          className={`wf-step-input${docMissing ? ' invalid' : ''}`}
                          placeholder="https://docs.google.com/..."
                          value={contentUrl}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [item.id]: { ...getDraft(item.id), contentUrl: e.target.value, contentText },
                            }))
                          }
                          onBlur={() => handleSaveRow(item.id)}
                        />
                        {docMissing && (
                          <div className="wf-step-field-error">A valid document link is required.</div>
                        )}
                      </td>
                      <td>
                        <textarea
                          className="wf-step-textarea"
                          placeholder="Notes or instructions for this site…"
                          value={contentText}
                          onChange={(e) =>
                            setDrafts((prev) => ({
                              ...prev,
                              [item.id]: { contentUrl, contentText: e.target.value },
                            }))
                          }
                          onBlur={() => handleSaveRow(item.id)}
                          style={{ minHeight: 60, resize: 'vertical' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="wf-step-action-bar">
            <span className="wf-step-action-bar-meta">
              {items.length} site{items.length !== 1 ? 's' : ''} in order
              {missingDocCount > 0 && (
                <span className="warn">
                  · {missingDocCount} missing doc link{missingDocCount !== 1 ? 's' : ''}
                </span>
              )}
            </span>
            <div className="wf-step-action-bar-btns">
              <button type="button" className="wf-btn-sec" onClick={() => goToStep('plan')}>
                Back to plan
              </button>
              <button
                type="button"
                className="wf-btn-create"
                title={allDocsProvided ? undefined : 'Add a document link for every site to continue'}
                style={{ opacity: allDocsProvided ? 1 : 0.55, cursor: allDocsProvided ? 'pointer' : 'not-allowed' }}
                onClick={() => {
                  if (!allDocsProvided) {
                    setAttemptedProceed(true);
                    return;
                  }
                  markStepDone('order');
                  if (usesPaymentCheckout) {
                    goToStep('cart');
                  } else {
                    setCheckoutOpen(true);
                  }
                }}
              >
                {usesPaymentCheckout ? 'Proceed to checkout' : 'Place order'}
              </button>
            </div>
          </div>
        </>
      )}

      {checkoutOpen && (
        <CheckoutModal
          open={checkoutOpen}
          onCheckoutSuccess={() => {
            checkoutSucceededRef.current = true;
            void fulfillSavedOrder();
            void queryClient.invalidateQueries({ queryKey: ['guestBlogSites'] });
            void queryClient.invalidateQueries({ queryKey: ['ordered-sites'] });
          }}
          onClose={() => {
            setCheckoutOpen(false);
            if (checkoutSucceededRef.current) {
              markStepDone('order');
              goToStep('status');
            }
          }}
          projectId={projectId}
          projectName={cart?.projectName || selectedProject?.projectName || 'Project'}
          clientName={cart?.clientName || selectedProject?.clientName || 'Client'}
          planId={planId || undefined}
        />
      )}
    </div>
  );
};

export default PlaceOrderPage;
